import { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Tag, Popconfirm, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, CrownOutlined, ShopOutlined } from '@ant-design/icons';
import API from '../api/client';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await API.get('/auth/users');
      setUsers(res.data);
    } catch { message.error('Kullanıcılar yüklenemedi'); }
    setLoading(false);
  };

  const fetchBusinesses = async () => {
    try {
      const res = await API.get('/businesses');
      setBusinesses(res.data);
    } catch {}
  };

  useEffect(() => {
    fetchUsers();
    fetchBusinesses();
  }, []);

  const openModal = (user = null) => {
    setEditing(user);
    if (user) {
      form.setFieldsValue({
        username: user.username,
        displayName: user.displayName,
        role: user.role,
        assignedBusinesses: user.assignedBusinesses || [],
        password: '',
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ role: 'restaurant_user', assignedBusinesses: [] });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        if (!values.password) delete values.password;
        await API.put(`/auth/users/${editing._id}`, values);
        message.success('Kullanıcı güncellendi');
      } else {
        await API.post('/auth/users', values);
        message.success('Kullanıcı oluşturuldu');
      }
      setModalOpen(false);
      fetchUsers();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      message.error(detail || 'Hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/auth/users/${id}`);
      message.success('Kullanıcı silindi');
      fetchUsers();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      message.error(detail || 'Silinemedi');
    }
  };

  const getBusinessName = (id) => {
    const biz = businesses.find(b => b._id === id);
    return biz ? biz.name : id;
  };

  const columns = [
    {
      title: 'Kullanıcı Adı',
      dataIndex: 'username',
      render: (text, record) => (
        <Space>
          {record.role === 'super_admin' ? <CrownOutlined style={{ color: '#D4A843' }} /> : <UserOutlined />}
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: 'Görünen Ad',
      dataIndex: 'displayName',
    },
    {
      title: 'Rol',
      dataIndex: 'role',
      render: (role) => (
        role === 'super_admin'
          ? <Tag color="gold" icon={<CrownOutlined />}>Süper Admin</Tag>
          : <Tag color="blue" icon={<ShopOutlined />}>Restoran Yetkilisi</Tag>
      ),
    },
    {
      title: 'Atanan İşletmeler',
      dataIndex: 'assignedBusinesses',
      render: (ids) => (
        ids && ids.length > 0
          ? ids.map(id => <Tag key={id} color="purple">{getBusinessName(id)}</Tag>)
          : <span style={{ color: '#999' }}>—</span>
      ),
    },
    {
      title: 'İşlemler',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openModal(record)} />
          {record.role !== 'super_admin' && (
            <Popconfirm title="Bu kullanıcıyı silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record._id)} okText="Evet" cancelText="Hayır">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const selectedRole = Form.useWatch('role', form);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Kullanıcı Yönetimi</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
          Yeni Kullanıcı
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="_id"
        loading={loading}
        pagination={false}
      />

      <Modal
        title={editing ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editing ? 'Güncelle' : 'Oluştur'}
        cancelText="İptal"
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="username" label="Kullanıcı Adı" rules={[{ required: true, message: 'Kullanıcı adı zorunlu' }]}>
            <Input prefix={<UserOutlined />} placeholder="kullanici_adi" />
          </Form.Item>
          <Form.Item name="displayName" label="Görünen Ad">
            <Input placeholder="Ali Yılmaz" />
          </Form.Item>
          <Form.Item
            name="password"
            label={editing ? 'Yeni Şifre (boş bırakırsan değişmez)' : 'Şifre'}
            rules={editing ? [] : [{ required: true, message: 'Şifre zorunlu' }]}
          >
            <Input.Password placeholder="••••••••" />
          </Form.Item>
          <Form.Item name="role" label="Rol" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'super_admin', label: '👑 Süper Admin (Tüm Yetkiler)' },
                { value: 'restaurant_user', label: '🏪 Restoran Yetkilisi (Sadece Menü)' },
              ]}
            />
          </Form.Item>
          {selectedRole === 'restaurant_user' && (
            <Form.Item name="assignedBusinesses" label="Yetkili Olduğu İşletmeler">
              <Select
                mode="multiple"
                placeholder="İşletme seçin..."
                options={businesses.map(b => ({ value: b._id, label: b.name }))}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default UsersPage;
